import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  input,
  Output,
  type OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatDatepickerModule,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MatSelectModule,
  type MatSelectChange,
} from '@angular/material/select';
import {
  getTimePeriodStartEnd,
  Timezone,
  type ITimePeriod,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { type Moment } from 'moment-timezone';
import { Subject } from 'rxjs';
import { TypedFormControl, TypedFormGroup } from '../forms/typed-form-group';
import { MOMENT_DATEPICKER_PROVIDERS } from '../moment-datepicker-providers';
import { NgCommonModule } from '../ng-common.module';
import { TrackByFunctions } from '../track-by';

@Component({
  selector: 'pt-date-range-form',
  templateUrl: './date-range-form.component.html',
  styleUrls: ['./date-range-form.component.scss'],
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInput,
    MatDatepickerModule,
    NgCommonModule,
  ],
})
export class DateRangeFormComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  trackByDate = TrackByFunctions.label<IPredefinedDate>();
  dateRange: TypedFormGroup<IDateRangeFormData> =
    new TypedFormGroup<IDateRangeFormData>({
      from: new TypedFormControl(
        moment().subtract({ days: 7 }),
        Validators.required
      ),
      to: new TypedFormControl(moment(), Validators.required),
      predefinedDate: new TypedFormControl(),
    });
  predefinedDates: IPredefinedDate[] = PREDEFINED_DATES;
  @Output()
  changed: EventEmitter<ITimePeriod> = new EventEmitter<ITimePeriod>();
  @Output()
  fromChange = new EventEmitter<Moment>();
  @Output()
  toChange = new EventEmitter<Moment>();

  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() useRangePicker: boolean = false;
  @Input() startDateLabel = 'Start Date';
  @Input() endDateLabel = 'End Date';
  @Input() timezone?: Timezone;
  disabled = input<boolean>(false);
  dateLabel = input('Enter a date range');
  predefinedDateActions = input(true, { transform: coerceBooleanProperty });

  constructor() {
    this.dateRange.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.submit());
    this.dateRange.controls.from.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.fromChange.emit(value?.startOf('day')));
    this.dateRange.controls.to.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.toChange.emit(value?.endOf('day')));

    toObservable(this.disabled)
      .pipe(takeUntilDestroyed())
      .subscribe((disabled) =>
        disabled
          ? this.dateRange.disable({ emitEvent: false })
          : this.dateRange.enable({ emitEvent: false })
      );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  submit(): void {
    if (
      this.dateRange.invalid ||
      !this.dateRange.value.from ||
      !this.dateRange.value.to
    ) {
      return;
    }

    this.changed.emit({
      from: this.dateRange.value.from.startOf('day'),
      to: this.dateRange.value.to.endOf('day'),
    });
  }

  @Input()
  set from(from: moment.Moment) {
    this.dateRange.controls.from.setValue(from);
  }

  @Input()
  set to(to: moment.Moment) {
    this.dateRange.controls.to.setValue(to);
  }

  @Input()
  set range(range: ITimePeriod) {
    this.dateRange.patchValue(range);
  }

  updatePredefinedDate(event: MatSelectChange): void {
    const predefinedDate = event.value as IPredefinedDate;
    this.setPredefinedDate(predefinedDate);
  }

  setPredefinedDate(
    predefinedDate: IPredefinedDate,
    picker?: MatDateRangePicker<Moment>
  ): void {
    this.dateRange.patchValue(predefinedDate.getDates(this.timezone));
    picker?.close();
  }
}

interface IDateRangeFormData extends ITimePeriod {
  predefinedDate: IPredefinedDate;
}

export interface IPredefinedDate {
  label: string;
  getDates: (timezone?: Timezone) => ITimePeriod;
}

// TODO: Build moments with timezone - CU-251edxw
const PREDEFINED_DATES: IPredefinedDate[] = [
  {
    label: 'Today',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment(),
          to: moment(),
        },
        timezone,
        'day'
      );
    },
  },
  {
    label: 'Yesterday',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment().subtract(1, 'day'),
          to: moment().subtract(1, 'day'),
        },
        timezone,
        'day'
      );
    },
  },
  {
    label: 'This Week',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment(),
          to: moment(),
        },
        timezone,
        'week'
      );
    },
  },
  {
    label: 'This Month',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment(),
          to: moment(),
        },
        timezone,
        'month'
      );
    },
  },
  {
    label: 'This Year',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment(),
          to: moment(),
        },
        timezone,
        'year'
      );
    },
  },
  {
    label: 'Last Week',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment().subtract(1, 'week'),
          to: moment().subtract(1, 'week'),
        },
        timezone,
        'week'
      );
    },
  },
  {
    label: 'Last Month',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment().subtract(1, 'month'),
          to: moment().subtract(1, 'month'),
        },
        timezone,
        'month'
      );
    },
  },
  {
    label: 'Last 3 Months',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment().subtract(3, 'months'),
          to: moment().subtract(1, 'month'),
        },
        timezone,
        'month'
      );
    },
  },
  {
    label: 'Last 7 Days',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment().subtract(8, 'days'),
          to: moment().subtract(1, 'day'),
        },
        timezone,
        'day'
      );
    },
  },
  {
    label: 'Last 30 Days',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment().subtract(31, 'day'),
          to: moment().subtract(1, 'day'),
        },
        timezone,
        'day'
      );
    },
  },
  {
    label: 'All Time',
    getDates: (timezone?: Timezone): ITimePeriod => {
      return getTimePeriodStartEnd(
        {
          from: moment().year(1900),
          to: moment().year(2100),
        },
        timezone,
        'year'
      );
    },
  },
];
